// Step2Evidence — text / camera / file evidence capture.
// Spec: docs/specs/location-tasks.md §8.5. PR L wires the camera + file
// + text flows; the framework handles enable-state + completion display.

import React from "react";
import { Card, CardHeader } from "../../../liff/components/Mobile.js";
import { FileText, Camera, Upload, CheckCircle2 } from "lucide-react";
import { parseR2KeyList, type WizardData } from "./types.js";

interface Props {
  data: WizardData;
  /** Text slot is controlled — WizardRunner curries the (text, onChange)
   *  pair into a no-arg slot. Step2Evidence just renders it; the hint /
   *  done indicator comes from `textHasContent` instead of server data. */
  renderText?:   () => React.ReactElement;
  renderCamera?: (ctx: { data: WizardData; onComplete: () => void }) => React.ReactElement;
  renderFile?:   (ctx: { data: WizardData; onComplete: () => void }) => React.ReactElement;
  onChanged: () => void;
  /** Drives the「已填寫」hint on the text section header. Comes from
   *  WizardRunner's pendingText (lives outside server state until
   *  handleSubmit fires). Optional — if not provided we never show
   *  the「已填寫」hint. */
  textHasContent?: boolean;
}

export default function Step2Evidence({ data, renderText, renderCamera, renderFile, onChanged, textHasContent }: Props): React.ReactElement {
  const t = data.task;
  const s = data.submission;
  const blocks: React.ReactNode[] = [];

  if (t.requires_text === 1) {
    // Text 是 controlled by WizardRunner — 不像 camera / file 有伺服器
    // 端 r2_key 的 done 狀態，我們用 pendingText 是否非空來算。輸入框
    // 永遠 visible（不切換成「已填寫」唯讀卡片），使用者可以隨時編
    // 輯直到按下提交。
    const done = !!textHasContent;
    blocks.push(
      <Card key="text">
        <CardHeader title="文字回應" hint={done ? "已填寫" : "必填"} />
        <SubStepHeader icon={<FileText className="h-4 w-4" />} done={done}
          label="請就本任務寫下現場觀察、處置、結果說明（500 字內）。提交時會與其他資料一起送出。" />
        {renderText ? renderText() : <NotImplementedCta />}
      </Card>
    );
  }
  if (t.requires_camera === 1) {
    const photos = parseR2KeyList(s.photo_r2_keys);
    const done = photos.length > 0;
    blocks.push(
      <Card key="cam">
        <CardHeader title="即時拍照" hint={done ? `${photos.length} 張` : "後鏡頭"} />
        <SubStepHeader icon={<Camera className="h-4 w-4" />} done={done}
          label="使用裝置鏡頭拍攝現場（不允許從相簿選照片）。" />
        {done && <p className="text-xs text-success inline-flex items-center gap-1 mb-2"><CheckCircle2 className="h-3.5 w-3.5" />已拍 {photos.length} 張</p>}
        {renderCamera ? renderCamera({ data, onComplete: onChanged }) : !done ? <NotImplementedCta /> : null}
      </Card>
    );
  }
  if (t.requires_file === 1) {
    const files = parseR2KeyList(s.file_r2_keys);
    const done = files.length > 0;
    blocks.push(
      <Card key="file">
        <CardHeader title="檔案上傳" hint={done ? `${files.length} 個` : "選填"} />
        <SubStepHeader icon={<Upload className="h-4 w-4" />} done={done}
          label="可上傳照片 / PDF / docx（每個 ≤ 10 MB）。" />
        {done && <p className="text-xs text-success inline-flex items-center gap-1 mb-2"><CheckCircle2 className="h-3.5 w-3.5" />已上傳 {files.length} 個</p>}
        {renderFile ? renderFile({ data, onComplete: onChanged }) : !done ? <NotImplementedCta /> : null}
      </Card>
    );
  }
  if (blocks.length === 0) {
    return (
      <Card>
        <p className="text-sm text-text-secondary">此任務未要求 Step 2 證據。</p>
      </Card>
    );
  }
  return <>{blocks}</>;
}

function SubStepHeader({ icon, label, done }: { icon: React.ReactNode; label: string; done: boolean }): React.ReactElement {
  return (
    <div className={`flex items-start gap-2 mb-3 ${done ? "opacity-70" : ""}`}>
      <span className={`mt-0.5 ${done ? "text-success" : "text-text-secondary"}`}>{icon}</span>
      <p className="text-xs text-text-secondary leading-relaxed">{label}</p>
    </div>
  );
}

function NotImplementedCta(): React.ReactElement {
  return (
    <button disabled className="block w-full h-11 rounded-xl bg-bg-raised text-text-muted text-sm font-medium">
      此證據流程尚未上線
    </button>
  );
}
